import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import type { useDisclosure } from '@chakra-ui/react'
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Field, Form, Formik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import useToast from '../../../hooks/useToast'
import type { UserSessionResponse } from '../../../interfaces'

type Props = Pick<ReturnType<typeof useDisclosure>, 'isOpen' | 'onClose'> & {
  user: UserSessionResponse
  ctaLabel: string
}

interface FormProps {
  clientNumber: string
  token: string
}

const CardUpdate = ({ user, isOpen, onClose, ctaLabel }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          onSubmit={async (values, actions) => {
            if (elements && stripe) {
              const card = elements.getElement(CardElement)
              if (card) {
                const { error, token } = await stripe.createToken(card)
                // If use is on free plan then card details are mandatory otherwise ignore
                if (error && !user.pricingPlan.isPaidPlan) {
                  actions.setErrors({ token: error.message })
                  return
                }

                if (!token) {
                  actions.setErrors({
                    token: 'Invalid card details.',
                  })
                  return
                }

                const response = await request.put(
                  `/api/billing/payment-source/${user.billingDetails._id}`,
                  {
                    token: token.id,
                    clientNumber: user.clientNumber,
                  },
                )

                if (request.error) {
                  let message = 'Error in updating card details.'
                  if (response?.message) {
                    message = response.message
                  }
                  toast({
                    description: message,
                    status: 'error',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                  return
                }

                toast({
                  description: t(
                    'card_update_success',
                    'Card updated successfully',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })

                onClose()

                setTimeout(() => {
                  window.location.href = '/dashboard/billing/subscriptions'
                }, 2000)
              }
            }
          }}
          initialValues={{
            clientNumber: user.clientNumber,
            token: '',
          }}
          validationSchema={Yup.object().shape({})}
        >
          {({ isSubmitting }) => (
            <Form>
              <ModalHeader>
                {ctaLabel}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack
                  alignItems="stretch"
                  justifyContent="flex-start"
                  spacing={4}
                  marginTop={4}
                >
                  <Field name="token">
                    {({ field, form }) => (
                      <FormControl
                        name={field.name}
                        isInvalid={Boolean(
                          form.errors[field.name] && form.touched[field.name],
                        )}
                      >
                        <FormLabel>
                          {t('card_details', 'Card details')}
                        </FormLabel>
                        <Box
                          borderRadius="base"
                          borderWidth="1px"
                          borderColor="#e6e7ea"
                          padding="6.9px 17px"
                        >
                          <CardElement
                            options={{
                              style: {
                                base: {
                                  fontSize: '15px',
                                  color: '#32325d',
                                },
                              },
                            }}
                          />
                        </Box>
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.token}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button type="submit" isLoading={isSubmitting}>
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default CardUpdate
